/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import {MatCheckboxChange} from "@angular/material/checkbox";
import {MatDialog} from "@angular/material/dialog";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {MatTableDataSource} from "@angular/material/table";
import {ConfigSectionComponent} from "@app/models/components/config-section.component";
import {RegistryContexts} from "@app/models/constants/registry-constants";
import {CommonState} from "@app/store/common/common.state";
import {PatientTreatmentPlanBaseState} from "@doctor-panel-modules/patient-treatment-plan/store/patient-treatment-plan-base.state";
import {hasActionsExecuting} from "@ngxs-labs/actions-executing";
import {Select, Store} from "@ngxs/store";
import {ClinicalDataType, TrecReminder} from "@trec/api/clinical-data";
import {RangeDatesUnix} from "@trec/api/common";
import {C3SnackbarStatusService} from "@trec/components/snackbar-status";
import {StoreAction} from "@trec/components/store";
import {addYears, subYears} from "date-fns";
import {Observable, of, Subscription} from "rxjs";
import {TpBaseActions} from "../../../store/patient-treatment-plan-base.actions";
import {EditExaminationDialogComponent} from "../../edit/edit-examination-dialog/edit-examination-dialog.component";
import {TaskDeleteDialogComponent} from "../../others/task-delete-dialog/task-delete-dialog.component";

@Component({
  selector: "c3-config-examinations",
  templateUrl: "./config-examinations.component.html",
  styleUrls: ["./config-examinations.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ConfigExaminationsComponent
  extends ConfigSectionComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @Select(PatientTreatmentPlanBaseState.examinations) examinations$: Observable<TrecReminder[]>;
  @Select(hasActionsExecuting([TpBaseActions.FetchExaminations, TpBaseActions.DeleteExamination]))
  loading$: Observable<boolean>;

  @Input()
  context: RegistryContexts[];

  // Table vars ///////////////////////////////////////////////////////////////////////////////////

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  /**
   * Show ended examinations.
   */
  showEnded = false;

  displayedColumns: string[] = ["title", "start_date", "end_date", "freqValue", "doctor", "actions"];
  dataSource: MatTableDataSource<TrecReminder> = new MatTableDataSource();

  // Chart vars ///////////////////////////////////////////////////////////////////////////////////

  readonly viewPeriod: RangeDatesUnix = {
    start: subYears(new Date(), 1).valueOf(),
    end: addYears(new Date(), 4).valueOf(),
  };

  // Others ///////////////////////////////////////////////////////////////////////////////////////

  viewType: "table" | "chart" = "table";

  readonly today = new Date().valueOf();

  get touched$(): Observable<boolean> {
    return of(true);
  }

  get valid$(): Observable<boolean> {
    return of(true);
  }

  private subscr: Subscription;

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef,
    private c3Statusbar: C3SnackbarStatusService
  ) {
    super();
  }

  ngOnInit() {
    this.store.dispatch(new TpBaseActions.FetchExaminations());

    // Override filter function
    this.dataSource.filterPredicate = (data: TrecReminder, filter: string) => {
      if (filter === "show-all") {
        // Also ended items
        return true;
      } else if (filter === "hide-ended") {
        return data.metadata.end_date > this.today;
      } else {
        return true;
      }
    };

    // Define how sort function can access data using keys
    this.dataSource.sortingDataAccessor = (data: TrecReminder, sortHeaderId: string) => {
      switch (sortHeaderId) {
        case "title":
          return data.data.title;
        case "start_date":
          return data.metadata.start_date;
        case "end_date":
          return data.metadata.end_date;
        case "freqValue":
          return `${data.data.regular.frequency.values[0]} ${data.data.regular.frequency.type}`;

        default:
          return "";
      }
    };

    // Setup default filter
    this.dataSource.filter = "hide-ended";
  }

  canEditOrDelete(doctorId?: string) {
    if (!doctorId) {
      return false;
    }
    const id = this.store.selectSnapshot(CommonState.userId);

    return id === doctorId;
  }

  ngAfterViewInit() {
    // Init table
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;

    this.subscr = this.examinations$.subscribe((examinations) => {
      this.dataSource.data = examinations;
      this.cd.markForCheck();
    });
  }

  // View and Edit ////////////////////////////////////////////////////////////////////////////////

  openEditDialog(examination: TrecReminder | null = null) {
    this.dialog.open(EditExaminationDialogComponent, {
      width: "90%",
      minWidth: "720px",
      data: examination,
    });
  }

  onDeleteExamination(examination: TrecReminder) {
    const dialogRef = this.dialog.open(TaskDeleteDialogComponent, {
      data: {itemType: ClinicalDataType.REMINDER},
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.store.dispatch(new TpBaseActions.DeleteExamination({examination})).subscribe(() => {
          this.c3Statusbar.showSuccess({message: `Esame eliminato`});
        });
      }
    });
  }

  toggleShowEnded(event: MatCheckboxChange) {
    this.showEnded = event.checked;

    if (this.showEnded) {
      // Show all
      this.dataSource.filter = "show-all";
    } else {
      this.dataSource.filter = "hide-ended";
    }
    this.cd.markForCheck();
  }

  onRefreshList() {
    this.store.dispatch(new TpBaseActions.FetchExaminations());
  }

  prepareSaveDataAction(): StoreAction | null {
    return null;
  }

  resetTouched(): void { }

  ngOnDestroy(): void {
    if (this.subscr) {
      this.subscr.unsubscribe();
    }
  }
}
